import React, { useState } from 'react';
import CodeBrowser from './CodeBrowser';

const CodeBrowserModal = ({ isOpen, onClose, onSelectRepo }) => {
  const [repoPath, setRepoPath] = useState('');
  const [savedRepos, setSavedRepos] = useState(() => {
    // Load saved repositories from localStorage
    const saved = localStorage.getItem('savedRepositories');
    return saved ? JSON.parse(saved) : [];
  });
  const [error, setError] = useState('');
  const [showBrowser, setShowBrowser] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);

  const handleAddRepo = () => {
    if (!repoPath.trim()) {
      setError('Please enter a repository path');
      return;
    }

    // Validate the path exists (this will be checked more thoroughly by the backend)
    if (savedRepos.includes(repoPath)) {
      setError('This repository is already in your list');
      return;
    }

    // Save to local storage
    const updatedRepos = [...savedRepos, repoPath];
    setSavedRepos(updatedRepos);
    localStorage.setItem('savedRepositories', JSON.stringify(updatedRepos));
    
    setRepoPath('');
    setError('');
  };

  const handleRemoveRepo = (repo) => {
    const updatedRepos = savedRepos.filter(r => r !== repo);
    setSavedRepos(updatedRepos);
    localStorage.setItem('savedRepositories', JSON.stringify(updatedRepos));
  };

  const handleSelectRepo = (repo) => {
    setSelectedRepo(repo);
    setShowBrowser(true);
  };
  
  const handleAnalyzeRepo = () => {
    if (selectedRepo) {
      onSelectRepo(selectedRepo);
      onClose();
    }
  };
  
  const handleAnalyzeFile = () => {
    if (currentFile) {
      onSelectRepo(currentFile);
      onClose();
    }
  };
  
  const handleFileSelected = (filePath) => {
    setCurrentFile(filePath);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Repository Browser</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!showBrowser ? (
          <div className="p-4 overflow-y-auto flex-1">
            <div className="mb-4">
              <label htmlFor="repoPath" className="block text-sm font-medium text-gray-700 mb-1">
                Local Repository Path
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="repoPath"
                  className="input flex-1"
                  placeholder="/path/to/your/repo"
                  value={repoPath}
                  onChange={(e) => setRepoPath(e.target.value)}
                />
                <button
                  className="btn btn-primary ml-2"
                  onClick={handleAddRepo}
                >
                  Add
                </button>
              </div>
              {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-100 p-2 border-b font-medium">
                Saved Repositories
              </div>
              {savedRepos.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">
                  No saved repositories yet
                </div>
              ) : (
                <ul className="divide-y">
                  {savedRepos.map((repo) => (
                    <li key={repo} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <span className="truncate flex-1">{repo}</span>
                      <div className="flex">
                        <button
                          onClick={() => handleSelectRepo(repo)}
                          className="text-primary-600 hover:text-primary-800 p-1"
                          title="Browse repository"
                        >
                          Browse
                        </button>
                        <button
                          onClick={() => {
                            onSelectRepo(repo);
                            onClose();
                          }}
                          className="text-green-600 hover:text-green-800 p-1 ml-2"
                          title="Analyze repository"
                        >
                          Analyze
                        </button>
                        <button
                          onClick={() => handleRemoveRepo(repo)}
                          className="text-red-600 hover:text-red-800 p-1 ml-2"
                          title="Remove repository"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 overflow-y-auto flex-1">
            <div className="mb-4 flex justify-between items-center">
              <button
                onClick={() => {
                  setShowBrowser(false);
                  setCurrentFile(null);
                }}
                className="flex items-center text-primary-600 hover:text-primary-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back to Repository List
              </button>
              
              <div className="flex space-x-2">
                <button
                  onClick={handleAnalyzeRepo}
                  className="btn btn-primary"
                  title="Analyze the entire repository"
                  disabled={!selectedRepo}
                >
                  Analyze Repository
                </button>
                <button
                  onClick={handleAnalyzeFile}
                  className="btn btn-secondary"
                  title="Analyze the selected file"
                  disabled={!currentFile}
                >
                  Analyze File
                </button>
              </div>
            </div>
            <CodeBrowser 
              repoPath={selectedRepo} 
              onFileSelected={handleFileSelected}
            />
          </div>
        )}

        <div className="border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeBrowserModal;